import { Component, computed, inject, input, Input, OnInit, output, signal, ChangeDetectionStrategy } from '@angular/core';

import { FormBackButtonComponent } from '../form-back-button/form-back-button.component';
import { FormContentComponent } from '../form-content/form-content.component';
import { FormOptionListComponent } from '../form-option-list/form-option-list.component';
import { FormSubmitButtonComponent } from '../form-submit-button/form-submit-button.component';
import {
    MatchmakingApiService,
    MatchmakingDataService, MatchmakingTranslateService,
    TrackingMatchmakingSnowplowService
} from 'matchmaking/services';
import {
    Application,
    ApplicationSpecificQuestions,
    CustomPageQuestionType,
    palletizingQuestions,
    StepContent,
    QuestionAnswers,
} from 'matchmaking/models';
import { DynamicQuestionComponent } from './components/dynamic-question/dynamic-question.component';
import { FormTextareaInputComponent } from './components/form-textarea-input/form-textarea-input.component';

@Component({
    selector: 'matchmaking-step-application-specific',
    imports: [FormBackButtonComponent, FormContentComponent, FormSubmitButtonComponent, DynamicQuestionComponent, FormTextareaInputComponent, FormOptionListComponent],
    templateUrl: './step-application-specific.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrl: './step-application-specific.component.scss'
})
export class StepApplicationSpecificComponent implements OnInit {
    translateService = inject(MatchmakingTranslateService);
    dataService = inject(MatchmakingDataService);
    apiService = inject(MatchmakingApiService);
    trackingService = inject(TrackingMatchmakingSnowplowService);

    content = input.required<StepContent>();

    customPageQuestions = signal<ApplicationSpecificQuestions | null>(null);

    translations = computed(() => this.translateService.translations());
    isCustomPage = computed(() => this.dataService.config()?.stepApplicationSpecific.customPage);
    selectedQuestions = computed(() => this.dataService.config()?.stepApplicationSpecific.questions);
    otherAnswer = computed(() => this.dataService.config()?.stepApplicationSpecific.otherText ?? '');
    isSubmitDisabled = computed(() => {
        if (this.isCustomPage()) {
            // All the questions are optional
            return false;
        }

        const hasNotAnswered = this.otherAnswer() == '';
        return hasNotAnswered;
    });

    ngOnInit() {
        const config = this.dataService.config();
        if (config) {
            this.trackingService.trackStepStart({ step_number: 3, step_name: config.currentStep });

            // In case of custom page, set questions dynamically here
            if (config.stepApplicationSpecific.customPage) {
                const customPages: CustomPageQuestionType = {
                    [Application.PalletizingAndDepalletizing]: palletizingQuestions,
                };

                this.customPageQuestions.set(
                    customPages[config.stepApplicationSpecific.customPage],
                );
            }
        }
    }

    setOtherQuestion(answer: string) {
        const currentStep = this.dataService.config()!.stepApplicationSpecific;

        this.dataService.updateConfig({
            stepApplicationSpecific: {
                ...currentStep,
                otherText: answer,
            },
        });
    }

    setQuestion(event: { question: QuestionAnswers; value: string }) {
        const currentQuestions = this.dataService.config()!.stepApplicationSpecific.questions;

        this.dataService.updateConfig({
            stepApplicationSpecific: {
                ...this.dataService.config()!.stepApplicationSpecific,
                questions: {
                    ...currentQuestions,
                    [event.question]: event.value,
                },
            },
        });
    }

    submit() {
        this.apiService.submit();
    }

    handleBack() {
        this.apiService.goToPreviousStep();
    }
}
